import { Decision, Evidence, SyncResult } from '../../types/audit-trail';

// IndexedDB database setup
const DB_NAME = 'ClearAuditTrail';
const DB_VERSION = 1;
const DECISIONS_STORE = 'decisions';
const EVIDENCE_STORE = 'evidence';
const SYNC_QUEUE_STORE = 'syncQueue';

interface SyncQueueItem {
  id: string;
  type: 'create' | 'update' | 'delete';
  data: Decision | Evidence;
  timestamp: Date;
  retryCount: number;
}

class OfflineManager {
  private db: IDBDatabase | null = null;
  private isOnline: boolean = navigator.onLine;
  private syncInProgress: boolean = false;
  private syncCallbacks: Array<(result: SyncResult) => void> = [];

  constructor() {
    this.initializeDB();
    this.setupOnlineListener();
  }

  private async initializeDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB initialized successfully');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create decisions store
        if (!db.objectStoreNames.contains(DECISIONS_STORE)) {
          const decisionsStore = db.createObjectStore(DECISIONS_STORE, { keyPath: 'id' });
          decisionsStore.createIndex('createdAt', 'createdAt');
          decisionsStore.createIndex('status', 'status');
          decisionsStore.createIndex('syncStatus', 'syncStatus');
          decisionsStore.createIndex('type', 'type');
          decisionsStore.createIndex('riskLevel', 'riskLevel');
          decisionsStore.createIndex('createdBy', 'createdBy');
        }

        // Create evidence store
        if (!db.objectStoreNames.contains(EVIDENCE_STORE)) {
          const evidenceStore = db.createObjectStore(EVIDENCE_STORE, { keyPath: 'id' });
          evidenceStore.createIndex('decisionId', 'decisionId');
          evidenceStore.createIndex('uploadStatus', 'uploadStatus');
        }

        // Create sync queue store
        if (!db.objectStoreNames.contains(SYNC_QUEUE_STORE)) {
          const syncStore = db.createObjectStore(SYNC_QUEUE_STORE, { keyPath: 'id' });
          syncStore.createIndex('timestamp', 'timestamp');
          syncStore.createIndex('type', 'type');
        }
      };
    });
  }

  private setupOnlineListener(): void {
    window.addEventListener('online', () => {
      this.isOnline = true;
      console.log('Connection restored - triggering sync');
      this.triggerAutoSync();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      console.log('Connection lost - switching to offline mode');
    });
  }

  // Decision management
  async saveDecision(decision: Decision): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([DECISIONS_STORE], 'readwrite');
      const store = transaction.objectStore(DECISIONS_STORE);
      
      // Update sync status based on connectivity
      const updatedDecision = {
        ...decision,
        syncStatus: this.isOnline ? 'pending' : 'pending' as const,
        updatedAt: new Date()
      };

      const request = store.put(updatedDecision);

      request.onsuccess = () => {
        // Add to sync queue if online
        if (this.isOnline) {
          this.addToSyncQueue('update', updatedDecision);
        }
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to save decision:', request.error);
        reject(request.error);
      };
    });
  }

  async getDecision(id: string): Promise<Decision | null> {
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([DECISIONS_STORE], 'readonly');
      const store = transaction.objectStore(DECISIONS_STORE);
      const request = store.get(id);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        console.error('Failed to get decision:', request.error);
        reject(request.error);
      };
    });
  }

  async getAllDecisions(filter?: any): Promise<Decision[]> {
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([DECISIONS_STORE], 'readonly');
      const store = transaction.objectStore(DECISIONS_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        let decisions = request.result as Decision[];
        
        // Apply client-side filtering
        if (filter) {
          decisions = this.applyFilter(decisions, filter);
        }

        // Sort by created date (newest first)
        decisions.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        
        resolve(decisions);
      };

      request.onerror = () => {
        console.error('Failed to get decisions:', request.error);
        reject(request.error);
      };
    });
  }

  async deleteDecision(id: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([DECISIONS_STORE], 'readwrite');
      const store = transaction.objectStore(DECISIONS_STORE);
      const request = store.delete(id);

      request.onsuccess = () => {
        // Add to sync queue for server deletion
        if (this.isOnline) {
          this.addToSyncQueue('delete', { id } as any);
        }
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to delete decision:', request.error);
        reject(request.error);
      };
    });
  }

  // Evidence management
  async saveEvidence(evidence: Evidence, file?: File): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    // Store file locally if provided
    if (file && !this.isOnline) {
      evidence.localPath = await this.storeFileLocally(file);
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([EVIDENCE_STORE], 'readwrite');
      const store = transaction.objectStore(EVIDENCE_STORE);
      const request = store.put(evidence);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to save evidence:', request.error);
        reject(request.error);
      };
    });
  }

  private async storeFileLocally(file: File): Promise<string> {
    // Convert file to base64 for local storage
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        const key = `file_${Date.now()}_${Math.random()}`;
        localStorage.setItem(key, base64);
        resolve(key);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async getLocalFile(localPath: string): Promise<string | null> {
    return localStorage.getItem(localPath);
  }

  // Sync queue management
  private async addToSyncQueue(type: 'create' | 'update' | 'delete', data: any): Promise<void> {
    if (!this.db) return;

    const queueItem: SyncQueueItem = {
      id: `${type}_${data.id}_${Date.now()}`,
      type,
      data,
      timestamp: new Date(),
      retryCount: 0
    };

    const transaction = this.db.transaction([SYNC_QUEUE_STORE], 'readwrite');
    const store = transaction.objectStore(SYNC_QUEUE_STORE);
    store.put(queueItem);
  }

  async getSyncQueue(): Promise<SyncQueueItem[]> {
    if (!this.db) return [];

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([SYNC_QUEUE_STORE], 'readonly');
      const store = transaction.objectStore(SYNC_QUEUE_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        console.error('Failed to get sync queue:', request.error);
        reject(request.error);
      };
    });
  }

  async clearSyncQueue(): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([SYNC_QUEUE_STORE], 'readwrite');
      const store = transaction.objectStore(SYNC_QUEUE_STORE);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Sync operations
  async triggerSync(): Promise<SyncResult> {
    if (this.syncInProgress) {
      throw new Error('Sync already in progress');
    }

    if (!this.isOnline) {
      throw new Error('Cannot sync while offline');
    }

    this.syncInProgress = true;

    try {
      const queueItems = await this.getSyncQueue();
      const result: SyncResult = {
        success: true,
        synced: 0,
        failed: 0,
        conflicts: [],
        errors: []
      };

      for (const item of queueItems) {
        try {
          await this.syncItem(item);
          result.synced++;
        } catch (error: any) {
          result.failed++;
          result.errors.push(error.message);
          
          // Check for conflicts
          if (error.type === 'conflict') {
            result.conflicts.push(error.conflict);
          }
        }
      }

      // Clear successfully synced items
      if (result.synced > 0) {
        await this.clearSyncQueue();
      }

      result.success = result.failed === 0;
      return result;

    } finally {
      this.syncInProgress = false;
    }
  }

  private async syncItem(item: SyncQueueItem): Promise<void> {
    const { type, data } = item;

    switch (type) {
      case 'create':
      case 'update':
        await this.syncDecisionToServer(data as Decision);
        break;
      case 'delete':
        await this.deleteDecisionFromServer(data.id);
        break;
      default:
        throw new Error(`Unknown sync type: ${type}`);
    }
  }

  private async syncDecisionToServer(decision: Decision): Promise<void> {
    const endpoint = decision.syncStatus === 'synced' 
      ? `/api/decisions/${decision.id}`
      : '/api/decisions';
    
    const method = decision.syncStatus === 'synced' ? 'PUT' : 'POST';

    const response = await fetch(endpoint, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`
      },
      body: JSON.stringify(decision)
    });

    if (!response.ok) {
      if (response.status === 409) {
        // Conflict - decision was modified on server
        const serverData = await response.json();
        throw {
          type: 'conflict',
          conflict: {
            decisionId: decision.id,
            field: 'entire_decision',
            localValue: decision,
            serverValue: serverData,
            lastModified: {
              local: decision.updatedAt,
              server: new Date(serverData.updatedAt)
            }
          }
        };
      }
      throw new Error(`Sync failed: ${response.statusText}`);
    }

    const syncedDecision = await response.json();
    
    // Update local copy with server data
    await this.saveDecision({
      ...syncedDecision,
      syncStatus: 'synced'
    });
  }

  private async deleteDecisionFromServer(id: string): Promise<void> {
    const response = await fetch(`/api/decisions/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${this.getAuthToken()}`
      }
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Delete failed: ${response.statusText}`);
    }
  }

  private getAuthToken(): string {
    // Get from localStorage or context
    return localStorage.getItem('authToken') || '';
  }

  private triggerAutoSync(): void {
    if (this.isOnline && !this.syncInProgress) {
      setTimeout(() => {
        this.triggerSync().then(result => {
          this.syncCallbacks.forEach(callback => callback(result));
        }).catch(error => {
          console.error('Auto-sync failed:', error);
        });
      }, 1000); // Small delay to ensure connection is stable
    }
  }

  onSyncComplete(callback: (result: SyncResult) => void): () => void {
    this.syncCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.syncCallbacks.indexOf(callback);
      if (index > -1) {
        this.syncCallbacks.splice(index, 1);
      }
    };
  }

  // Utility methods
  private applyFilter(decisions: Decision[], filter: any): Decision[] {
    return decisions.filter(decision => {
      if (filter.dateFrom && new Date(decision.createdAt) < filter.dateFrom) return false;
      if (filter.dateTo && new Date(decision.createdAt) > filter.dateTo) return false;
      if (filter.types && !filter.types.includes(decision.type)) return false;
      if (filter.riskLevels && !filter.riskLevels.includes(decision.riskLevel)) return false;
      if (filter.statuses && !filter.statuses.includes(decision.status)) return false;
      if (filter.syncStatus && !filter.syncStatus.includes(decision.syncStatus)) return false;
      if (filter.createdBy && !filter.createdBy.includes(decision.createdBy)) return false;
      if (filter.searchTerm) {
        const searchTerm = filter.searchTerm.toLowerCase();
        const searchable = `${decision.title} ${decision.decisionMade} ${decision.rationale}`.toLowerCase();
        if (!searchable.includes(searchTerm)) return false;
      }
      
      return true;
    });
  }

  // Status methods
  isOffline(): boolean {
    return !this.isOnline;
  }

  isSyncInProgress(): boolean {
    return this.syncInProgress;
  }

  async getPendingSyncCount(): Promise<number> {
    const queue = await this.getSyncQueue();
    return queue.length;
  }

  async getStorageInfo(): Promise<{used: number, quota: number}> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        used: estimate.usage || 0,
        quota: estimate.quota || 0
      };
    }
    return { used: 0, quota: 0 };
  }
}

// Create singleton instance
export const offlineManager = new OfflineManager();
export default OfflineManager;